import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, User, Search, UserCheck, UserX, Save } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface CustomerDataDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orderId: string | null;
  restaurantId: string | undefined;
  /** Called when customer is identified/saved without an existing order. */
  onIdentifiedWithoutOrder?: (data: {
    customerId: string | null;
    customerCpf: string | null;
    name: string | null;
  }) => void;
  /** Called when the identification is removed and there is no existing order. */
  onClearedWithoutOrder?: () => void;
}

// CPF mask: XXX.XXX.XXX-XX
function formatCPF(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
}

function isValidCPF(cpf: string): boolean {
  const digits = cpf.replace(/\D/g, "");
  if (digits.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(digits)) return false;
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += parseInt(digits[i]) * (10 - i);
  let remainder = (sum * 10) % 11;
  if (remainder === 10) remainder = 0;
  if (remainder !== parseInt(digits[9])) return false;
  sum = 0;
  for (let i = 0; i < 10; i++) sum += parseInt(digits[i]) * (11 - i);
  remainder = (sum * 10) % 11;
  if (remainder === 10) remainder = 0;
  if (remainder !== parseInt(digits[10])) return false;
  return true;
}

function formatCEP(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
}

export function CustomerDataDialog({
  open,
  onOpenChange,
  orderId,
  restaurantId,
  onIdentifiedWithoutOrder,
  onClearedWithoutOrder,
}: CustomerDataDialogProps) {
  const [cpf, setCpf] = useState("");
  const [nome, setNome] = useState("");
  const [endereco, setEndereco] = useState("");
  const [complemento, setComplemento] = useState("");
  const [bairro, setBairro] = useState("");
  const [cidade, setCidade] = useState("");
  const [estado, setEstado] = useState("");
  const [cep, setCep] = useState("");
  const [cpfError, setCpfError] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [foundCustomerId, setFoundCustomerId] = useState<string | null>(null);
  const [customerFound, setCustomerFound] = useState(false);
  const [currentLinkedCpf, setCurrentLinkedCpf] = useState<string | null>(null);

  // Load existing customer data from order when dialog opens
  useEffect(() => {
    if (open && orderId) {
      loadOrderCustomerData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, orderId]);

  const loadOrderCustomerData = async () => {
    if (!orderId) return;
    const { data } = await supabase
      .from("orders")
      .select("customer_id, customer_cpf")
      .eq("id", orderId)
      .single();

    if (data?.customer_cpf) {
      setCpf(formatCPF(data.customer_cpf));
      setCurrentLinkedCpf(data.customer_cpf);
      // Try to load customer details
      if (data.customer_id) {
        const { data: customer } = await supabase
          .from("customers")
          .select("*")
          .eq("id", data.customer_id)
          .single();
        if (customer) {
          setNome(customer.name || "");
          setEndereco(customer.address || "");
          setComplemento(customer.address_complement || "");
          setBairro(customer.neighborhood || "");
          setCidade(customer.city || "");
          setEstado(customer.state || "");
          setCep(customer.postal_code ? formatCEP(customer.postal_code) : "");
          setFoundCustomerId(customer.id);
          setCustomerFound(true);
        }
      }
    } else {
      resetForm();
    }
  };

  const resetForm = () => {
    setCpf("");
    setNome("");
    setEndereco("");
    setComplemento("");
    setBairro("");
    setCidade("");
    setEstado("");
    setCep("");
    setCpfError("");
    setFoundCustomerId(null);
    setCustomerFound(false);
    setCurrentLinkedCpf(null);
  };

  // Auto-search customer by CPF
  const searchCustomerByCpf = useCallback(async (cpfDigits: string) => {
    if (!restaurantId || cpfDigits.length !== 11) return;
    if (!isValidCPF(cpfDigits)) {
      setCpfError("CPF inválido");
      return;
    }
    setCpfError("");
    setIsSearching(true);
    try {
      const { data } = await supabase
        .from("customers")
        .select("*")
        .eq("restaurant_id", restaurantId)
        .eq("cpf", cpfDigits)
        .maybeSingle();

      if (data) {
        setNome(data.name || "");
        setEndereco(data.address || "");
        setComplemento(data.address_complement || "");
        setBairro(data.neighborhood || "");
        setCidade(data.city || "");
        setEstado(data.state || "");
        setCep(data.postal_code ? formatCEP(data.postal_code) : "");
        setFoundCustomerId(data.id);
        setCustomerFound(true);
        toast.success("Cliente encontrado!");
      } else {
        setFoundCustomerId(null);
        setCustomerFound(false);
      }
    } catch {
      // ignore
    } finally {
      setIsSearching(false);
    }
  }, [restaurantId]);

  const handleCpfChange = (value: string) => {
    const formatted = formatCPF(value);
    setCpf(formatted);
    setCpfError("");
    setCustomerFound(false);
    setFoundCustomerId(null);

    const digits = formatted.replace(/\D/g, "");
    if (digits.length === 11) {
      searchCustomerByCpf(digits);
    }
  };

  // Save and link customer
  const handleSaveAndLink = async () => {
    const cpfDigits = cpf.replace(/\D/g, "");
    if (cpfDigits.length > 0 && cpfDigits.length !== 11) {
      setCpfError("CPF deve ter 11 dígitos");
      return;
    }
    if (cpfDigits.length === 11 && !isValidCPF(cpfDigits)) {
      setCpfError("CPF inválido");
      return;
    }
    if (!nome.trim()) {
      toast.error("Nome é obrigatório para cadastrar cliente");
      return;
    }
    if (!restaurantId) return;

    setIsSaving(true);
    try {
      const cepDigits = cep.replace(/\D/g, "") || null;
      const customerData = {
        name: nome.trim(),
        cpf: cpfDigits || null,
        address: endereco.trim() || null,
        address_complement: complemento.trim() || null,
        neighborhood: bairro.trim() || null,
        city: cidade.trim() || null,
        state: estado.trim() || null,
        postal_code: cepDigits,
      };

      let customerId = foundCustomerId;

      if (customerId) {
        // Update existing customer
        await supabase
          .from("customers")
          .update(customerData)
          .eq("id", customerId);
      } else {
        // Create new customer
        const { data, error } = await supabase
          .from("customers")
          .insert({
            restaurant_id: restaurantId,
            ...customerData,
            source: "pdv",
          })
          .select("id")
          .single();
        if (error) throw error;
        customerId = data.id;
      }

      if (orderId) {
        // Link to existing order
        await supabase
          .from("orders")
          .update({
            customer_id: customerId,
            customer_cpf: cpfDigits || null,
          })
          .eq("id", orderId);
        toast.success("Cliente salvo e vinculado ao pedido!");
      } else {
        // No order yet — defer linking until the first order is sent
        onIdentifiedWithoutOrder?.({
          customerId,
          customerCpf: cpfDigits || null,
          name: nome.trim() || null,
        });
        toast.success("Cliente identificado! Será vinculado ao enviar o pedido.");
      }
      onOpenChange(false);
    } catch (err: any) {
      toast.error("Erro ao salvar cliente: " + (err.message || ""));
    } finally {
      setIsSaving(false);
    }
  };

  // Identify only (NFC-e) - just link CPF/name to order
  const handleIdentifyOnly = async () => {
    const cpfDigits = cpf.replace(/\D/g, "");
    if (cpfDigits.length > 0 && cpfDigits.length !== 11) {
      setCpfError("CPF deve ter 11 dígitos");
      return;
    }
    if (cpfDigits.length === 11 && !isValidCPF(cpfDigits)) {
      setCpfError("CPF inválido");
      return;
    }
    setIsSaving(true);
    try {
      if (orderId) {
        await supabase
          .from("orders")
          .update({
            customer_id: foundCustomerId || null,
            customer_cpf: cpfDigits || null,
          })
          .eq("id", orderId);
        toast.success("Identificação vinculada ao pedido!");
      } else {
        onIdentifiedWithoutOrder?.({
          customerId: foundCustomerId || null,
          customerCpf: cpfDigits || null,
          name: nome.trim() || null,
        });
        toast.success("Identificação registrada! Será vinculada ao enviar o pedido.");
      }
      onOpenChange(false);
    } catch {
      toast.error("Erro ao vincular identificação");
    } finally {
      setIsSaving(false);
    }
  };

  // Remove identification
  const handleRemoveIdentification = async () => {
    setIsSaving(true);
    try {
      if (orderId) {
        await supabase
          .from("orders")
          .update({
            customer_id: null,
            customer_cpf: null,
          })
          .eq("id", orderId);
      } else {
        onClearedWithoutOrder?.();
      }
      resetForm();
      toast.success("Identificação removida!");
      onOpenChange(false);
    } catch {
      toast.error("Erro ao remover identificação");
    } finally {
      setIsSaving(false);
    }
  };

  const cpfDigits = cpf.replace(/\D/g, "");
  const hasValidCpf = cpfDigits.length === 11 && isValidCPF(cpfDigits);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg bg-zinc-900 border-zinc-700 text-zinc-100 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-zinc-100">
            <User className="h-5 w-5" />
            Dados do Consumidor
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Identifique o cliente para NFC-e e programa de fidelidade.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* CPF */}
          <div className="space-y-1.5">
            <Label htmlFor="cpf-data" className="text-zinc-300">CPF</Label>
            <div className="relative">
              <Input
                id="cpf-data"
                placeholder="000.000.000-00"
                value={cpf}
                onChange={(e) => handleCpfChange(e.target.value)}
                disabled={isSaving}
                maxLength={14}
                className={`bg-zinc-800 border-zinc-600 text-zinc-100 placeholder:text-zinc-500 ${cpfError ? "border-red-500" : ""} ${customerFound ? "border-green-500" : ""}`}
              />
              {isSearching && (
                <Loader2 className="absolute right-3 top-2.5 h-4 w-4 animate-spin text-zinc-400" />
              )}
              {customerFound && !isSearching && (
                <UserCheck className="absolute right-3 top-2.5 h-4 w-4 text-green-400" />
              )}
            </div>
            {cpfError && <p className="text-sm text-red-400">{cpfError}</p>}
            {customerFound && <p className="text-sm text-green-400">Cliente encontrado!</p>}
          </div>

          {/* Nome */}
          <div className="space-y-1.5">
            <Label htmlFor="nome-data" className="text-zinc-300">Nome Completo</Label>
            <Input
              id="nome-data"
              placeholder="Nome do cliente"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              disabled={isSaving}
              maxLength={100}
              className="bg-zinc-800 border-zinc-600 text-zinc-100 placeholder:text-zinc-500"
            />
          </div>

          {/* Endereço */}
          <div className="space-y-1.5">
            <Label htmlFor="endereco-data" className="text-zinc-300">Endereço</Label>
            <Input
              id="endereco-data"
              placeholder="Rua, número"
              value={endereco}
              onChange={(e) => setEndereco(e.target.value)}
              disabled={isSaving}
              maxLength={200}
              className="bg-zinc-800 border-zinc-600 text-zinc-100 placeholder:text-zinc-500"
            />
          </div>

          {/* Complemento + Bairro */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="complemento-data" className="text-zinc-300">Complemento</Label>
              <Input
                id="complemento-data"
                placeholder="Apto, bloco..."
                value={complemento}
                onChange={(e) => setComplemento(e.target.value)}
                disabled={isSaving}
                maxLength={100}
                className="bg-zinc-800 border-zinc-600 text-zinc-100 placeholder:text-zinc-500"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bairro-data" className="text-zinc-300">Bairro</Label>
              <Input
                id="bairro-data"
                placeholder="Bairro"
                value={bairro}
                onChange={(e) => setBairro(e.target.value)}
                disabled={isSaving}
                maxLength={100}
                className="bg-zinc-800 border-zinc-600 text-zinc-100 placeholder:text-zinc-500"
              />
            </div>
          </div>

          {/* Cidade + Estado + CEP */}
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="cidade-data" className="text-zinc-300">Cidade</Label>
              <Input
                id="cidade-data"
                placeholder="Cidade"
                value={cidade}
                onChange={(e) => setCidade(e.target.value)}
                disabled={isSaving}
                maxLength={100}
                className="bg-zinc-800 border-zinc-600 text-zinc-100 placeholder:text-zinc-500"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="estado-data" className="text-zinc-300">Estado</Label>
              <Input
                id="estado-data"
                placeholder="UF"
                value={estado}
                onChange={(e) => setEstado(e.target.value.toUpperCase())}
                disabled={isSaving}
                maxLength={2}
                className="bg-zinc-800 border-zinc-600 text-zinc-100 placeholder:text-zinc-500"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cep-data" className="text-zinc-300">CEP</Label>
              <Input
                id="cep-data"
                placeholder="00000-000"
                value={cep}
                onChange={(e) => setCep(formatCEP(e.target.value))}
                disabled={isSaving}
                maxLength={9}
                className="bg-zinc-800 border-zinc-600 text-zinc-100 placeholder:text-zinc-500"
              />
            </div>
          </div>
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <div className="flex gap-2 w-full">
            <Button
              onClick={handleSaveAndLink}
              disabled={isSaving || !nome.trim()}
              className="flex-1 bg-green-600 hover:bg-green-700 text-white"
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Salvar e Vincular
            </Button>
            <Button
              onClick={handleIdentifyOnly}
              disabled={isSaving || !hasValidCpf}
              variant="outline"
              className="flex-1 border-zinc-600 text-zinc-300 hover:bg-zinc-800"
            >
              <User className="h-4 w-4 mr-2" />
              Apenas NFC-e
            </Button>
          </div>
          {currentLinkedCpf && (
            <Button
              onClick={handleRemoveIdentification}
              disabled={isSaving}
              variant="ghost"
              className="w-full text-red-400 hover:text-red-300 hover:bg-red-900/20"
            >
              <UserX className="h-4 w-4 mr-2" />
              Remover Identificação
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
